/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */

import {Injectable, signal} from '@angular/core';

/**
 * Service for sharing the currently selected content language across all
 * language-picker instances rendered on a passport view (e.g. multi-language
 * document lists, translated field values).
 *
 * This is unrelated to the application UI language (see LanguageService):
 * it tracks which language variant of the passport's own data is currently
 * displayed, independent of the UI's display language.
 *
 * Public surface is intentionally closed to exactly these members:
 * `selectedLanguage`, `overrides`, `switchAll()`, `setInstanceOverride()`,
 * `reset()`. Nothing here persists beyond the current view (no
 * sessionStorage/localStorage/backend) — `reset()` is expected to be called
 * exactly once per fresh passport view.
 */
@Injectable({
  providedIn: 'root',
})
export class ContentLanguageSelectionService {
  private readonly selectedLanguageSignal = signal<string | null>(null);
  private readonly overridesSignal = signal<Map<string, string>>(new Map());

  /** The page-wide selected content language code, or null if none was selected yet. */
  readonly selectedLanguage = this.selectedLanguageSignal.asReadonly();

  /** Per-instance language overrides, keyed by the instance's `fullValuePath`. */
  readonly overrides = this.overridesSignal.asReadonly();

  /**
   * Sets the content language for the whole page and clears every Instance
   * Override, so all instances re-evaluate against the new Selected Language.
   *
   * @param language - Language code to select (for example: en, de).
   */
  switchAll(language: string): void {
    this.selectedLanguageSignal.set(language);
    this.overridesSignal.set(new Map());
  }

  /**
   * Sets a language override for a single instance, identified by its
   * `fullValuePath`. Only that instance is affected; the page's Selected
   * Language and all other overrides are left untouched.
   *
   * @param path - The instance's `fullValuePath`.
   * @param language - Language code to use for that instance.
   */
  setInstanceOverride(path: string, language: string): void {
    const updated = new Map(this.overridesSignal());
    updated.set(path, language);
    this.overridesSignal.set(updated);
  }

  /**
   * Clears the Selected Language and every Instance Override, so a fresh
   * passport view starts from scratch with no carryover.
   */
  reset(): void {
    this.selectedLanguageSignal.set(null);
    this.overridesSignal.set(new Map());
  }
}
